import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, EVIDENCE } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { UploadSimple, Sparkle, Trash, FileText, CheckCircle, XCircle } from "@phosphor-icons/react";

const CONF = { high: "#22C55E", medium: "#F59E0B", low: "#EF4444" };
const STATUS = { uploaded: "Uploaded", extracting: "Extracting…", extracted: "Extracted", failed: "Extraction failed" };

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [depts, setDepts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = () => Promise.all([api.get("/documents"), api.get("/departments")]).then(([d, dp]) => {
    setDocs(d.data); setDepts(dp.data);
    if (d.data.some((x) => x.status === "extracting")) {
      clearTimeout(pollRef.current);
      pollRef.current = setTimeout(load, 4000);
    }
  });
  useEffect(() => { load(); return () => clearTimeout(pollRef.current); }, []);

  const deptName = Object.fromEntries(depts.map((d) => [d.key, d.name]));
  const deptColor = Object.fromEntries(depts.map((d) => [d.key, d.color]));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded — run Copilot extraction when ready`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const extract = async (id) => {
    try {
      await api.post(`/documents/${id}/extract`);
      toast.info("Copilot is analysing the document — proposals will appear for your review (30–90s)");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const decide = async (docId, propId, decision) => {
    try {
      await api.post(`/documents/${docId}/proposals/${propId}/${decision}`);
      toast.success(decision === "approve" ? "Approved — added to the model with AI-inference evidence status" : "Proposal rejected");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id, filename) => {
    if (!window.confirm(`Delete "${filename}" and all its proposals? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="documents-page">
      <PageHeader overline="Evidence & Ingestion" title="Documents"
        subtitle="Upload strategy documents, org charts and SOPs. The Copilot proposes capabilities, workflows and roles with reporting lines — each with source quotes and confidence. Nothing enters the model without your named approval."
        actions={
          <>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md,.csv" className="hidden" data-testid="document-file-input"
              onChange={(e) => upload(e.target.files[0])} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-document-btn"
              className="flex items-center gap-2 text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2.5 font-semibold hover:bg-black/85 transition-colors disabled:opacity-50">
              <UploadSimple size={15} /> {uploading ? "Uploading…" : "Upload document"}
            </button>
          </>
        } />
      <div className="px-8 space-y-5">
        {docs === null && <p className="text-black/50 text-sm">Loading…</p>}
        {docs?.length === 0 && (
          <div className="bg-white border border-dashed border-black/20 rounded-sm p-10 text-center" data-testid="documents-empty-state">
            <FileText size={32} className="mx-auto text-black/25" />
            <p className="font-display font-bold mt-3">No documents yet</p>
            <p className="text-[13px] text-black/50 mt-1">Upload a strategy document, org chart export or SOP (PDF, DOCX, TXT, MD or CSV, max 10MB) and let the Copilot draft capabilities, workflows and roles for review.</p>
          </div>
        )}
        {docs?.map((doc) => (
          <div key={doc.id} className="bg-white border border-black/10 rounded-sm" data-testid={`document-card-${doc.id}`}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/8">
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={20} className="text-[#D4AF37] shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-[14px] truncate">{doc.filename}</p>
                  <p className="text-[11px] text-black/45">{(doc.size_bytes / 1024).toFixed(0)} KB · {doc.chars.toLocaleString()} chars · {doc.uploaded_by} · {new Date(doc.uploaded_at).toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm ${doc.status === "extracted" ? "bg-green-100 text-green-700" : doc.status === "extracting" ? "bg-amber-100 text-amber-700 animate-pulse" : doc.status === "failed" ? "bg-red-100 text-red-700" : "bg-black/5 text-black/60"}`}
                  data-testid={`document-status-${doc.id}`}>{STATUS[doc.status]}</span>
                {doc.status !== "extracting" && (
                  <button onClick={() => extract(doc.id)} data-testid={`extract-btn-${doc.id}`}
                    className="flex items-center gap-1 text-[11px] bg-[#D4AF37] text-[#0A0A0A] rounded-sm px-3 py-1.5 font-semibold hover:bg-[#e0c258] transition-colors">
                    <Sparkle size={12} weight="fill" /> {doc.status === "uploaded" ? "Extract with Copilot" : "Re-extract"}
                  </button>
                )}
                <button onClick={() => remove(doc.id, doc.filename)} data-testid={`delete-document-${doc.id}`}
                  className="text-black/30 hover:text-red-500 transition-colors"><Trash size={15} /></button>
              </div>
            </div>
            {doc.status === "failed" && <p className="px-5 py-3 text-[12px] text-red-600">Extraction failed: {doc.error}. Try re-extracting.</p>}
            {doc.proposals?.length > 0 && (
              <div className="divide-y divide-black/5">
                {doc.proposals.map((p) => (
                  <div key={p.id} className={`px-5 py-3 ${p.status !== "pending" ? "opacity-60" : ""}`} data-testid={`proposal-${p.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm text-white ${p.kind === "capability" ? "bg-[#6366F1]" : p.kind === "role" ? "bg-[#14B8A6]" : "bg-[#F97316]"}`}>{p.kind}</span>
                          <span className="font-medium text-[13px]">{p.payload.name || p.payload.title}</span>
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm text-white" style={{ background: CONF[p.confidence] }}>confidence {p.confidence}</span>
                          <span className="text-[10px]" style={{ color: EVIDENCE.ai_inference.color }}>● AI inference</span>
                        </div>
                        {p.kind === "capability" ? (
                          <p className="text-[12px] text-black/60 mt-1">
                            <span style={{ color: deptColor[p.payload.department_key] }}>● </span>{deptName[p.payload.department_key]} · maturity {p.payload.maturity_current} · {p.payload.description}
                          </p>
                        ) : p.kind === "role" ? (
                          <p className="text-[12px] text-black/60 mt-1">
                            <span style={{ color: deptColor[p.payload.department_key] }}>● </span>{deptName[p.payload.department_key]}{p.payload.headcount ? ` · ${p.payload.headcount} people` : ""}{p.payload.reports_to ? ` · reports to ${p.payload.reports_to}` : ""} · {p.payload.tasks.length} task{p.payload.tasks.length === 1 ? "" : "s"} mapped
                          </p>
                        ) : (
                          <p className="text-[12px] text-black/60 mt-1">{p.payload.steps.length} steps across {[...new Set(p.payload.steps.map((s) => s.department_key))].length} departments · trigger: {p.payload.trigger}</p>
                        )}
                        {p.source_quote && <p className="text-[11px] text-black/45 italic mt-1 border-l-2 border-[#D4AF37] pl-2">"{p.source_quote}"</p>}
                        {p.status !== "pending" && <p className="text-[11px] mt-1" style={{ color: p.status === "approved" ? "#16A34A" : "#DC2626" }}>{p.status} by {p.decided_by}{p.status === "approved" && p.kind === "workflow" && p.created_id && <Link to={`/workflows/${p.created_id}`} className="underline ml-2">open workflow</Link>}{p.status === "approved" && p.kind === "capability" && <Link to="/capabilities" className="underline ml-2">view capabilities</Link>}{p.status === "approved" && p.kind === "role" && <Link to="/work-roles" className="underline ml-2">view roles</Link>}</p>}
                      </div>
                      {p.status === "pending" && (
                        <div className="flex gap-2 shrink-0">
                          <button onClick={() => decide(doc.id, p.id, "approve")} data-testid={`approve-proposal-${p.id}`}
                            className="flex items-center gap-1 text-[11px] bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-3 py-1.5 font-semibold hover:bg-black/85 transition-colors"><CheckCircle size={12} /> Approve</button>
                          <button onClick={() => decide(doc.id, p.id, "reject")} data-testid={`reject-proposal-${p.id}`}
                            className="flex items-center gap-1 text-[11px] border border-black/20 rounded-sm px-3 py-1.5 hover:border-red-400 hover:text-red-600 transition-colors"><XCircle size={12} /> Reject</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {doc.status === "extracted" && doc.proposals?.length === 0 && (
              <p className="px-5 py-3 text-[12px] text-black/50">Copilot found no capabilities or workflows it could support with evidence from this document.</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
